/**
 * Villa Glory — Meta browser events + Conversions API relay.
 * Uses the Facebook & Instagram app's top-level fbq (runtime OPEN); never calls fbq('init').
 * Contact  : method=whatsapp|phone|email; any click whose closest <a> is wa.me / api.whatsapp.com / whatsapp:// / tel: / mailto:
 * Lead     : only after the CRM form reports success ('vg-crm:success'), never on localization/cart/search.
 *            product-enquiry and contact-message Leads are fired by vg-crm-forms.js with the server's
 *            meta_event_id; the CRM routes send the server Lead (with user data), so Lead is never relayed here.
 * ViewCollection : /collections/{handle} — browser only (the relay accepts only Contact/Lead/ViewContent/Schedule;
 *            a server ViewContent would not deduplicate against a browser ViewCollection).
 * Every relayed event uses the SAME event_id for the pixel (eventID) and the relay (event_id), and the SAME event name.
 * The relay call is fire-and-forget: it never throws, never blocks, and fails silently (e.g. 404 before deploy).
 * external_id (added 2026-09-26): first-party anonymous id in cookie _vg_xid (random UUID, ~13 months, only when
 *            marketing is allowed). Browser: added to the app's EXISTING pixel via fbq('init', sameId, {external_id})
 *            (fbevents merges user data and hashes it; no new pixel, no extra PageView). Server: sent raw to the relay
 *            and the CRM forms as external_id; the CRM hashes it (sha256 of the lowercased value) before Meta.
 * _fbc (added 2026-09-26): on a landing URL with ?fbclid=, if the pixel has not stored that click id in _fbc
 *            by the time it is ready (or after 8 s), set _fbc = fb.1.<ms>.<fbclid> (90 days) ourselves.
 */
(function () {
  'use strict';
  var DEFAULT_RELAY = 'https://admin.villagloryfurniture.com/api/public/meta-event';
  var RELAY = '';
  try {
    RELAY = (typeof window.VG_META_RELAY_URL === 'string' && window.VG_META_RELAY_URL.trim()) || DEFAULT_RELAY;
    if (window.VG_META_RELAY_DISABLED === true) RELAY = '';           // kill switch
  } catch (e) { RELAY = ''; }
  var SERVER_EVENTS = { Contact: 1, ViewContent: 1, Schedule: 1 };   // Lead is sent by the CRM form routes
  var debug = /(?:^|[?&])fbdebug=1(?:&|$)/.test(location.search || '');
  var last = {};
  var PIXEL_ID = '1258044955705618';   // the Facebook & Instagram app pixel; only ever enriched, never created
  var XID_COOKIE = '_vg_xid';
  var XID_RE = /^[a-z0-9-]{8,64}$/;

  function uuid() {
    try { if (window.crypto && crypto.randomUUID) return crypto.randomUUID(); } catch (e) {}
    return Date.now() + '-' + Math.random().toString(36).slice(2, 11);
  }
  function cookie(n) {
    try {
      var m = document.cookie.match(new RegExp('(?:^|; )' + n + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : '';
    } catch (e) { return ''; }
  }
  function setCookie(n, v, days) {
    try {
      var dom = /(^|\.)villagloryfurniture\.com$/.test(location.hostname || '') ? '; domain=.villagloryfurniture.com' : '';
      document.cookie = n + '=' + encodeURIComponent(v) + '; max-age=' + Math.round(days * 86400) + '; path=/' + dom +
        '; SameSite=Lax' + (location.protocol === 'https:' ? '; Secure' : '');
    } catch (e) {}
  }
  /* first-party anonymous id; '' when marketing is not allowed or cookies cannot be stored (never a per-page id) */
  function externalId() {
    try {
      var v = cookie(XID_COOKIE);
      if (XID_RE.test(v)) return v;
      if (!marketingAllowed()) return '';
      v = String(uuid()).toLowerCase();
      if (!XID_RE.test(v)) return '';
      setCookie(XID_COOKIE, v, 395);
      return cookie(XID_COOKIE) === v ? v : '';
    } catch (e) { return ''; }
  }
  /* ?fbclid= landing: store fb.1.<ms>.<fbclid> in _fbc unless the pixel already stored this click id */
  function ensureFbc() {
    try {
      if (!marketingAllowed()) return;
      var id = new URLSearchParams(location.search || '').get('fbclid');
      if (!id || !/^[A-Za-z0-9_.-]{1,450}$/.test(id)) return;
      var c = cookie('_fbc');
      if (c && c.split('.').slice(3).join('.') === id) return;
      setCookie('_fbc', 'fb.1.' + Date.now() + '.' + id, 90);
    } catch (e) {}
  }
  var amDone = false;
  function pixelReady() {
    try {
      if (typeof window.fbq !== 'function' || typeof window.fbq.getState !== 'function') return false;
      var ps = window.fbq.getState().pixels || [];
      for (var i = 0; i < ps.length; i++) if (ps[i] && String(ps[i].id) === PIXEL_ID) return true;
    } catch (e) {}
    return false;
  }
  /* browser advanced matching: add external_id to the app's already-initialised pixel (once per page) */
  function advancedMatching() {
    try {
      if (amDone || !pixelReady()) return;
      var x = externalId();
      if (!x) return;
      amDone = true;
      window.fbq('init', PIXEL_ID, { external_id: x });
    } catch (e) {}
  }
  function fbc() {
    var c = cookie('_fbc');
    if (c) return c;
    try {
      var id = new URLSearchParams(location.search || '').get('fbclid');
      if (id && /^[A-Za-z0-9_.-]{1,450}$/.test(id)) return 'fb.1.' + Date.now() + '.' + id;
    } catch (e) {}
    return '';
  }
  function marketingAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.marketingAllowed === 'function') return cp.marketingAllowed();
    } catch (e) {}
    return true; // no consent API loaded → same behaviour as the app pixel
  }
  function pageLabel() {
    var p = (location.pathname || '/').replace(/\/+$/, '') || '/';
    if (p.indexOf('/products/') === 0) return 'Product Page';
    if (p.indexOf('/collections/') === 0) return 'Collection Page';
    if (p.indexOf('/pages/enquire') === 0) return 'Enquire Page';
    if (p.indexOf('/pages/stores') === 0) return 'Stores Page';
    return p === '/' ? 'Home Page' : p;
  }

  /* Server relay — text/plain (no CORS preflight), no credentials, keepalive; all errors swallowed. */
  function relay(eventName, id, custom) {
    try {
      if (!RELAY || !SERVER_EVENTS[eventName] || !id || !marketingAllowed()) return;
      var p = { event_name: eventName, event_id: id, event_source_url: location.href };
      var fbp = cookie('_fbp'), c = fbc(), x = externalId();
      if (fbp) p.fbp = fbp;
      if (c) p.fbc = c;
      if (x) p.external_id = x;
      if (custom) p.custom_data = custom;
      var body = JSON.stringify(p);
      if (window.fetch) {
        window.fetch(RELAY, {
          method: 'POST', mode: 'no-cors', credentials: 'omit', keepalive: true, cache: 'no-store',
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' }, body: body
        }).catch(function () {});
      } else if (navigator.sendBeacon) {
        navigator.sendBeacon(RELAY, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
      }
      if (debug) console.log('[vg-meta-events] relay', eventName, id);
    } catch (e) {}
  }

  function send(eventName, custom, opts) {
    try {
      opts = opts || {};
      var key = eventName + '|' + (custom.content_name || '');
      var now = Date.now();
      if (last[key] && now - last[key] < 1000) return null;
      last[key] = now;
      var id = opts.eventId || uuid();

      advancedMatching();
      try {
        if (typeof window.fbq === 'function') {
          if (opts.custom) window.fbq('trackCustom', eventName, custom, { eventID: id });
          else window.fbq('track', eventName, custom, { eventID: id });
        } else if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) {
          // fallback if Meta ever moves the app pixel into a sandbox
          Shopify.analytics.publish('vg_meta_' + eventName.toLowerCase(), { event_id: id, custom_data: custom });
        }
      } catch (e) {}

      if (!opts.custom && opts.relay !== false) relay(eventName, id, custom);
      if (debug) console.log('[vg-meta-events]', eventName, id, custom);
      return id;
    } catch (e) { return null; }
  }

  window.VGMeta = window.VGMeta || { uuid: uuid, fbp: function () { return cookie('_fbp'); }, fbc: fbc, relay: relay, externalId: externalId };

  /* once the app pixel is initialised (or after 8 s): external_id matching, then the _fbc fallback */
  (function waitPixel(n) {
    if (pixelReady() || n >= 40) { advancedMatching(); ensureFbc(); return; }
    setTimeout(function () { waitPixel(n + 1); }, 200);
  })(0);

  /* Contact — resolve the anchor with closest(), so clicks on inner <span>/<svg> count */
  document.addEventListener('click', function (e) {
    try {
      var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (!a) return;
      var h = (a.getAttribute('href') || '').toLowerCase();
      var method = null;
      if (h.indexOf('wa.me') !== -1 || h.indexOf('api.whatsapp.com') !== -1 || h.indexOf('whatsapp://') === 0) method = 'whatsapp';
      else if (h.indexOf('tel:') === 0) method = 'phone';
      else if (h.indexOf('mailto:') === 0) method = 'email';
      if (!method) return;
      var placement = a.getAttribute('data-vg-wa-context') ||
        (a.classList.contains('vg-concierge-floating') ? 'floating' : (a.closest('footer, .vg-footer') ? 'footer' : 'inline'));
      var label = { whatsapp: 'WhatsApp', phone: 'Phone', email: 'Email' }[method];
      send('Contact', { content_name: label + ' - ' + pageLabel(), method: method, placement: placement });
    } catch (err) {}
  }, true);

  /* Lead — the event_id is written into the form as a hidden field BEFORE submit (capture phase),
     so vg-crm-forms.js can send it to the CRM (server Lead) and reuse it for the browser Lead. */
  document.addEventListener('submit', function (e) {
    try {
      var f = e.target;
      if (!f || !f.matches || !f.matches('[data-vg-crm-form="product-enquiry"],[data-vg-crm-form="contact-message"]')) return;
      var input = f.querySelector('input[name="meta_event_id"]');
      if (!input) { input = document.createElement('input'); input.type = 'hidden'; input.name = 'meta_event_id'; f.appendChild(input); }
      input.value = uuid();
      f.setAttribute('data-vg-meta-eid', input.value);
    } catch (err) {}
  }, true);
  document.addEventListener('vg-crm:success', function (e) {
    try {
      var f = e.target;
      var kind = f.getAttribute('data-vg-crm-form');
      if (kind !== 'product-enquiry' && kind !== 'contact-message') return;
      // vg-crm-forms.js fires the single browser Lead with the server's meta_event_id
      if ((e.detail && e.detail.meta_lead_fired) || window.VG_CRM_FIRES_LEAD === true) return;
      // legacy vg-crm-forms.js only: browser Lead with the id it sent; the server route sends its own Lead → no relay
      send('Lead', { content_name: (kind === 'product-enquiry' ? 'Product Enquiry' : 'Consultation Form') + ' - ' + pageLabel(),
                     content_category: kind }, { eventId: f.getAttribute('data-vg-meta-eid') || undefined, relay: false });
    } catch (err) {}
  });

  /* ViewCollection — browser only (see header) */
  try {
    var m = (location.pathname || '').match(/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/([^\/?#]+)/i);
    if (m && m[1] !== 'all') {
      var tries = 0;
      (function wait() {
        if (typeof window.fbq === 'function' || tries++ >= 25) {
          send('ViewCollection', { content_name: 'Collection - ' + m[1], content_category: m[1], content_type: 'product_group' }, { custom: true });
          return;
        }
        setTimeout(wait, 200);
      })();
    }
  } catch (e) {}
})();

/**
 * Villa Glory — GA4 custom events (added 2026-09-26). Independent of the Meta code above.
 * GA4 (G-G1QK9BSE5H) is loaded by the Google & YouTube app in the top-level page (gtag + dataLayer,
 * page_view sent by the app with send_page_view:false configs). We only send custom events, addressed
 * with send_to so they go to the GA4 property only (not the GT-/MC- destinations). No config, no page_view.
 *   generate_lead  { form_name }        after a successful CRM submit ('vg-crm:success'), never on click
 *   whatsapp_click { button_location }  any click whose closest <a> is wa.me / api.whatsapp.com / whatsapp://
 */
(function () {
  'use strict';
  var GA4_ID = (typeof window.VG_GA4_ID === 'string' && window.VG_GA4_ID) || 'G-G1QK9BSE5H';

  function analyticsAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') return cp.analyticsProcessingAllowed();
    } catch (e) {}
    return true; // no consent API loaded → same as the Google app (consent mode applies inside gtag)
  }
  function ga4(name, params) {
    try {
      if (window.VG_GA4_DISABLED === true || !analyticsAllowed()) return;
      // gtag (GA4) already sends via navigator.sendBeacon / fetch keepalive, so navigation is never blocked;
      // transport_type is not passed because GA4 would record it as an extra event parameter.
      var p = { send_to: GA4_ID };
      for (var k in params) if (Object.prototype.hasOwnProperty.call(params, k)) p[k] = params[k];
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, p);
      } else {
        window.dataLayer = window.dataLayer || [];
        (function () { window.dataLayer.push(arguments); })('event', name, p); // queued for gtag.js
      }
    } catch (e) {}
  }
  function pageType() {
    var p = (location.pathname || '/').replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/)/i, '');
    if (p === '/' || p === '') return 'home_page';
    if (p.indexOf('/products/') === 0) return 'product_page';
    if (p.indexOf('/collections/') === 0) return 'collection_page';
    if (/^\/pages\/(enquire|contact)/.test(p)) return 'contact_page';
    if (p.indexOf('/pages/stores') === 0) return 'stores_page';
    if (p.indexOf('/pages/') === 0) return 'page_' + p.slice(7).split('/')[0].replace(/[^a-z0-9_-]/gi, '').slice(0, 40);
    if (p.indexOf('/blogs/') === 0) return 'journal_page';
    return 'other';
  }
  function buttonLocation(a) {
    var ctx = (a.getAttribute('data-vg-wa-context') || '').toLowerCase();
    if (ctx === 'floating' || a.classList.contains('vg-concierge-floating')) return 'floating';
    if (ctx === 'header' || a.closest('header, .vg-header')) return 'header';
    if (a.closest('footer, .vg-footer')) return 'footer';
    if (ctx === 'product') return 'product_page';
    if (ctx === 'stores') return 'stores_page';
    if (ctx === 'contact') return 'contact_page';
    return pageType(); // 'default' / unlabelled inline buttons → the page they sit on
  }

  /* whatsapp_click — capture phase, closest() so inner <span>/<svg> taps count; never prevents navigation */
  document.addEventListener('click', function (e) {
    try {
      var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (!a) return;
      var h = (a.getAttribute('href') || '').toLowerCase();
      if (h.indexOf('wa.me') === -1 && h.indexOf('api.whatsapp.com') === -1 && h.indexOf('whatsapp://') !== 0) return;
      ga4('whatsapp_click', { button_location: buttonLocation(a) });
    } catch (err) {}
  }, true);

  /* generate_lead — only lead forms, only after the CRM success response (vg-crm-forms.js dispatches
     'vg-crm:success' only when the API answered success). Honeypot-filled (bot) submits are skipped. */
  var FORM_NAMES = { 'vg-consultation-quote': 'consultation', 'vg-stores-visit': 'store_visit', 'vg-product-quote': 'product_enquiry' };
  document.addEventListener('submit', function (e) {
    try {
      var f = e.target;
      if (!f || !f.matches || !f.matches('[data-vg-crm-form]')) return;
      var hp = f.elements && (f.elements.namedItem('vg_trap_7f') || f.elements.namedItem('_hp'));
      f.__vgGaHp = !!(hp && String(hp.value || '').trim());
    } catch (err) {}
  }, true);
  document.addEventListener('vg-crm:success', function (e) {
    try {
      var f = e.target;
      var kind = f && f.getAttribute && f.getAttribute('data-vg-crm-form');
      if (kind !== 'product-enquiry' && kind !== 'contact-message') return; // not career applications
      if (f.__vgGaHp) return;
      ga4('generate_lead', { form_name: FORM_NAMES[f.id] || (kind === 'product-enquiry' ? 'product_enquiry' : 'contact') });
    } catch (err) {}
  });
})();

/**
 * Villa Glory — GA4 generate_lead for the Trade Program application (added 2026-09-26).
 * That form is Shopify's native {% form 'contact' %} (snippet vg-designer-application-form, hidden
 * contact[form_type]=designer_application, id vg-trade-program-application / vg-designer-application).
 * Shopify POSTs to /contact and, on success, redirects back with ?contact_posted=true and renders
 * <p class="vg-quote-form__success"> inside the form (errors render .vg-quote-form__errors instead).
 * Fires ONLY when: (1) this tab submitted a trade form within the last 30 min (sessionStorage marker set
 * on submit), (2) the page is back with contact_posted=true, and (3) the success element is rendered
 * inside that same trade form and no error block is. The marker is consumed on fire, so refreshing or
 * re-opening the success URL does not fire again. Press/newsletter and other /contact forms never set it.
 */
(function () {
  'use strict';
  var GA4_ID = (typeof window.VG_GA4_ID === 'string' && window.VG_GA4_ID) || 'G-G1QK9BSE5H';
  var KEY = 'vg_ga4_trade_pending';
  var TRADE_IDS = { 'vg-trade-program-application': 1, 'vg-designer-application': 1 };
  var MAX_AGE = 30 * 60 * 1000; // allows for a Shopify captcha challenge between submit and success

  function store() { try { var s = window.sessionStorage; s.getItem(KEY); return s; } catch (e) { return null; } }
  function isTradeForm(f) {
    return !!(f && f.id && TRADE_IDS[f.id] && f.querySelector &&
      f.querySelector('input[name="contact[form_type]"][value="designer_application"]'));
  }
  function analyticsAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') return cp.analyticsProcessingAllowed();
    } catch (e) {}
    return true;
  }
  function ga4(name, params) {
    try {
      if (window.VG_GA4_DISABLED === true || !analyticsAllowed()) return;
      var p = { send_to: GA4_ID };
      for (var k in params) if (Object.prototype.hasOwnProperty.call(params, k)) p[k] = params[k];
      if (typeof window.gtag === 'function') window.gtag('event', name, p);
      else { window.dataLayer = window.dataLayer || []; (function () { window.dataLayer.push(arguments); })('event', name, p); }
    } catch (e) {}
  }

  /* submit (after HTML5 validation passed) → remember that THIS tab submitted a trade form; nothing is sent */
  document.addEventListener('submit', function (e) {
    try {
      var f = e.target;
      if (!isTradeForm(f)) return;
      var s = store();
      if (s) s.setItem(KEY, JSON.stringify({ id: f.id, t: Date.now() }));
    } catch (err) {}
  }, true);

  /* page load → fire once if Shopify rendered the trade form's success state */
  function check() {
    try {
      var s = store();
      if (!s) return;
      var raw = s.getItem(KEY);
      if (!raw) return;
      var p = null;
      try { p = JSON.parse(raw); } catch (e) {}
      if (!p || !p.id || !p.t || Date.now() - p.t > MAX_AGE) { s.removeItem(KEY); return; }
      var f = document.getElementById(p.id);
      if (!isTradeForm(f)) return;                                    // not back on the trade page (e.g. captcha)
      if (f.querySelector('.vg-quote-form__errors')) { s.removeItem(KEY); return; } // Shopify reported errors
      if (!/(?:^|[?&])contact_posted=true(?:&|$)/.test((location.search || '').slice(1))) return;
      if (!f.querySelector('.vg-quote-form__success')) return;
      s.removeItem(KEY);                                              // consume: at most once per success
      ga4('generate_lead', { form_name: 'trade_program' });
    } catch (err) {}
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', check);
  else check();
})();

/**
 * Villa Glory — Meta Lead for Shopify native enquiry forms (added 2026-09-27).
 * Until 2026-09-25 ~11:30 (Dubai) custom pixel 212795761 fired Lead on EVERY form_submitted; since then Lead
 * fired only for CRM enquiries (vg-crm-forms.js + CRM route). This restores Lead for the Shopify
 * {% form 'contact' %} enquiry forms only, and only after Shopify confirms success:
 *   contact[form_type] = professional_request | material_library_request | designer_application | quote_request
 * Never: press_newsletter, careers (CRM career-application), search, cart, localization.
 * Flow: submit (validation passed) → sessionStorage marker {form id, form_type, event_id, time};
 * page load with ?contact_posted=true AND .vg-quote-form__success inside that same form AND no error block
 * → ONE browser Lead (eventID = event_id) + ONE relay Lead to the CRM (/api/public/meta-event, same
 * event_id) so Meta deduplicates. Marker consumed on fire (refresh does not refire); 30-min max age.
 * Kill switches: window.VG_META_SHOPIFY_LEAD_DISABLED = true (whole block), VG_META_RELAY_DISABLED (server half).
 */
(function () {
  'use strict';
  if (window.VG_META_SHOPIFY_LEAD_DISABLED === true) return;
  var KEY = 'vg_meta_lead_pending';
  var MAX_AGE = 30 * 60 * 1000;
  var RELAY = 'https://admin.villagloryfurniture.com/api/public/meta-event';
  var TYPES = {
    professional_request: 'Professional Enquiry',
    material_library_request: 'Material Library Request',
    designer_application: 'Trade Program Application',
    quote_request: 'Quote Request'
  };
  function store() { try { var s = window.sessionStorage; s.getItem(KEY); return s; } catch (e) { return null; } }
  function formType(f) {
    try {
      var i = f && f.querySelector && f.querySelector('input[name="contact[form_type]"]');
      var v = i ? String(i.value || '') : '';
      return Object.prototype.hasOwnProperty.call(TYPES, v) ? v : '';
    } catch (e) { return ''; }
  }
  function uuid() {
    try { if (window.crypto && crypto.randomUUID) return crypto.randomUUID(); } catch (e) {}
    return Date.now() + '-' + Math.random().toString(36).slice(2, 11);
  }
  function cookie(n) {
    try { var m = document.cookie.match(new RegExp('(?:^|; )' + n + '=([^;]*)')); return m ? decodeURIComponent(m[1]) : ''; }
    catch (e) { return ''; }
  }
  function marketingAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.marketingAllowed === 'function') return cp.marketingAllowed();
    } catch (e) {}
    return true;
  }
  function relayLead(id, custom) {
    try {
      if (window.VG_META_RELAY_DISABLED === true || !marketingAllowed()) return;
      var M = window.VGMeta || {};
      var p = { event_name: 'Lead', event_id: id, event_source_url: location.href, custom_data: custom };
      var fbp = cookie('_fbp'), c = M.fbc ? M.fbc() : cookie('_fbc'), x = M.externalId ? M.externalId() : '';
      if (fbp) p.fbp = fbp;
      if (c) p.fbc = c;
      if (x) p.external_id = x;
      var body = JSON.stringify(p);
      if (window.fetch) {
        window.fetch(RELAY, { method: 'POST', mode: 'no-cors', credentials: 'omit', keepalive: true, cache: 'no-store',
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' }, body: body }).catch(function () {});
      } else if (navigator.sendBeacon) {
        navigator.sendBeacon(RELAY, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
      }
    } catch (e) {}
  }

  /* submit → remember THIS tab submitted an enquiry form (nothing is sent yet) */
  document.addEventListener('submit', function (e) {
    try {
      var f = e.target;
      var ft = formType(f);
      if (!ft || !f.id) return;
      var s = store();
      if (s) s.setItem(KEY, JSON.stringify({ id: f.id, ft: ft, eid: uuid(), t: Date.now() }));
    } catch (err) {}
  }, true);

  /* page load → fire once if Shopify rendered that form's success state */
  function check() {
    try {
      var s = store();
      if (!s) return;
      var raw = s.getItem(KEY);
      if (!raw) return;
      var p = null;
      try { p = JSON.parse(raw); } catch (e) {}
      if (!p || !p.id || !p.ft || !p.eid || !p.t || Date.now() - p.t > MAX_AGE || !TYPES[p.ft]) { s.removeItem(KEY); return; }
      var f = document.getElementById(p.id);
      if (!f || formType(f) !== p.ft) return;                         // not back on that page (e.g. captcha)
      if (f.querySelector('.vg-quote-form__errors')) { s.removeItem(KEY); return; }
      if (!/(?:^|[?&])contact_posted=true(?:&|$)/.test((location.search || '').slice(1))) return;
      if (!f.querySelector('.vg-quote-form__success')) return;
      s.removeItem(KEY);                                              // consume: at most once per success
      var custom = { content_name: TYPES[p.ft], content_category: p.ft, lead_source: 'shopify_contact_form' };
      var tries = 0;
      (function fire() {
        if (typeof window.fbq === 'function' || tries++ >= 25) {
          try { if (typeof window.fbq === 'function') window.fbq('track', 'Lead', custom, { eventID: p.eid }); } catch (e) {}
          relayLead(p.eid, custom);
          if (/(?:^|[?&])fbdebug=1(?:&|$)/.test(location.search || '')) console.log('[vg-meta-events] shopify-form Lead', p.eid, custom);
          return;
        }
        setTimeout(fire, 200);
      })();
    } catch (err) {}
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', check);
  else check();
})();
